import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';

import { HttpClient } from '@angular/common/http';
import { throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { isPlainObject, omit } from 'lodash';

import { HelpersService } from '@base-shared/services/helpers.service';

@Injectable()
export class AppHttpClient {

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private helpers: HelpersService
  ) {

  }

  /**
  * GET Request
  * @param url request url
  */
  get(url, params?) {
    //return this.http.get(url, this.getOptions())
    return this.http.get(url, { params: params, withCredentials: false })
      .pipe(
        map(this.extractData),
        catchError(error => this.handleError(error))
      );
  }

  /**
  * POST Request
  * @param url request url
  * @param payload payload object
  * @param [contentType] optional content type header
  */
  post(url, payload, contentType?: string) {
    let customOptions = null;

    if (contentType) {
      const headers = new HttpHeaders({
        'Content-Type': contentType
      });
      customOptions = this.setCustomOptions(headers);
    }

    return this.http.post(url, payload, this.getOptions(customOptions))
      .pipe(
        map(this.extractData),
        catchError(error => this.handleError(error))
      );
  }

  /**
  * PATCH Request
  * @param url request url
  * @param payload payload object
  * @param [contentType] optional content type header
  */
  patch(url, payload, contentType?: string) {
    let customOptions = null;

    if (contentType) {
      const headers = new HttpHeaders({
        'Content-Type': contentType
      });
      customOptions = this.setCustomOptions(headers);
    }

    return this.http.patch(url, payload, this.getOptions(customOptions))
      .pipe(
        map(this.extractData),
        catchError(error => this.handleError(error))
      );
  }

  /**
  * PUT Request
  * @param url request url
  * @param payload payload object
  * @param [contentType] optional content type header
  */
  put(url, payload, contentType?: string) {
    let customOptions = null;

    if (contentType) {
      const headers = new HttpHeaders({
        'Content-Type': contentType
      });
      customOptions = this.setCustomOptions(headers);
    }

    return this.http.put(url, payload, this.getOptions(customOptions))
      .pipe(
        map(this.extractData),
        catchError(error => this.handleError(error))
      );
  }

  /**
  * DELETE Request
  * @param url request url
  * @param payload payload object
  */
  delete(url) {
    return this.http.delete(url, this.getOptions())
      .pipe(
        map(this.extractData),
        catchError(error => this.handleError(error))
      );
  }

  /**
   * Request options
   */
  private getOptions(customOptions?: any) {
    const options: any = {
      withCredentials: false
    };

    if (customOptions)
      options.headers = customOptions.headers || '';

    return options;
  }

  /**
  * Extract response from each request
  * @param res Endpoint response
  */
  private extractData(res: any) {
    let body;
    // validate if response is not json
    try {
      body = res.json() || {};
    } catch (error) {
      body = res;
    }
    return body;
  }

  /**
 * Handle error if it's present on request
 * @param error Response error
 */
  private handleError(error: Response | any, from?: any) {
    const message = this.errorMessage(this.getError(error), error.status);
    return throwError(message);
  }

  /**
   * Get error from body
   * @param error error description
   */
  private getError(error: any) {
    let body = error;
    try {
      body = error.json() || {};
      body = isPlainObject(body.error) ? body.error :
        isPlainObject(body.error.error) ? body.error.error : body;
    } catch (e) {
      error = omit(error, ['headers', 'toString', 'json', 'blob', 'arrayBuffer', 'text']);
      body = error;
    }

    return body;
  }

  /**
   * Create an object with custom options
   * @param headers http headers
   */
  private setCustomOptions(headers?: HttpHeaders) {
    return {
      headers: headers || ''
    };
  }

  /**
 * Get message from HTTP error
 * or generate a custome one if status match an option
 * @param error Response Error
 * @param status Http code
 */
  private errorMessage(error: any, status?: any) {
    let message: any = {};

    switch (status) {
      case 404:
        message = {
          ...error,
          message: this.translate.instant('common.not-found')
        };
        break;
      case 401:
      case 403:
        message = {
          ...error,
          message: this.translate.instant('common.not-authorized'),
        };
        break;
      case 500:
      case 405:
        message = {
          ...error,
          message: this.translate.instant('common.defaultServerError'),
        };
        break;

      default:
        message = error;
        break;
    }
    return message;
  }

}
